import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, X, Trash2, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { TimelineEvent, EventType, LegalSignificance } from '../../../models/TimelineEvent';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { DBTimelineEventThunks } from '../../../redux/thunks/DBServiceThunks';

interface EventEditFormProps {
  event?: TimelineEvent; // Optional - if undefined, it's create mode
  onCancel: () => void;
  onSave: () => void;
  onDelete?: () => void; // Optional - only for edit mode
  // Create mode props
  createMode?: boolean;
  ownerId?: string;
  caseId?: string;
  knowledgebaseId?: string;
  timelineId?: string;
}

const eventTypes: EventType[] = [
  'filing', 'hearing', 'trial', 'motion', 'ruling', 'judgment', 'appeal',
  'contract_signed', 'contract_amendment', 'notice', 'correspondence', 'evidence_submitted',
  'deadline', 'statute_of_limitations', 'discovery_deadline',
  'payment', 'delivery', 'breach', 'termination',
  'incident', 'complaint', 'investigation', 'settlement',
  'meeting', 'other'
];

const significanceLevels: LegalSignificance[] = ['high', 'medium', 'low'];

export default function EventEditForm({
  event,
  onCancel,
  onSave,
  onDelete,
  createMode = false,
  ownerId,
  caseId,
  knowledgebaseId,
  timelineId
}: EventEditFormProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Validate and ensure eventType is valid
  const getValidEventType = (type: string): EventType => {
    return eventTypes.includes(type as EventType) ? (type as EventType) : 'other';
  };

  // Validate and ensure legalSignificance is valid
  const getValidSignificance = (sig: string | undefined): LegalSignificance | undefined => {
    if (!sig) return undefined;
    return significanceLevels.includes(sig as LegalSignificance) ? (sig as LegalSignificance) : undefined;
  };

  // Get first document for create mode
  const allDocuments = useAppSelector(state =>
    Object.values(state.DBDocumentReducer.entities).filter(doc => doc?.knowledgebaseId === knowledgebaseId)
  );
  const firstDocument = createMode ? allDocuments[0] : null;

  const [title, setTitle] = useState(event?.title || '');
  const [description, setDescription] = useState(event?.description || '');
  const [startDate, setStartDate] = useState(
    event?.startDate ? event.startDate.split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [startTime, setStartTime] = useState(event?.startTime || '');
  const [endDate, setEndDate] = useState(event?.endDate ? event.endDate.split('T')[0] : '');
  const [endTime, setEndTime] = useState(event?.endTime || '');
  const [eventType, setEventType] = useState<EventType>(
    event ? getValidEventType(event.eventType) : 'other'
  );
  const [legalSignificance, setLegalSignificance] = useState<LegalSignificance | undefined>(
    event ? getValidSignificance(event.legalSignificance) : undefined
  );
  const [parties, setParties] = useState<string[]>(event?.partiesInvolved || []);
  const [newParty, setNewParty] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleAddParty = () => {
    if (newParty.trim()) {
      setParties([...parties, newParty.trim()]);
      setNewParty('');
    }
  };

  const handleRemoveParty = (index: number) => {
    setParties(parties.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (createMode && !title.trim()) {
      alert(`${t('common.timeline.edit.title')} is required`);
      return;
    }

    setIsSaving(true);
    try {
      // Convert dates back to ISO format
      const convertToDateTime = (dateStr: string, timeStr?: string): string => {
        const time = timeStr || '00:00';
        return `${dateStr}T${time}:00.000Z`;
      };

      if (createMode) {
        // Create new event
        const documentId = firstDocument?._id || knowledgebaseId!;
        const entryId = documentId;

        await dispatch(DBTimelineEventThunks.create({
          ownerId: ownerId!,
          caseId: caseId!,
          knowledgebaseId: knowledgebaseId!,
          timelineId: timelineId!,
          documentId,
          entryId,
          startDate: convertToDateTime(startDate, startTime),
          startTime: startTime || undefined,
          endDate: endDate ? convertToDateTime(endDate, endTime) : undefined,
          endTime: endTime || undefined,
          dateConfidence: 1.0,
          dateSource: 'explicit' as const,
          title: title.trim(),
          description: description.trim() || title.trim(),
          eventType,
          sourceText: description.trim() || title.trim(),
          startOffset: 0,
          endOffset: (description.trim() || title.trim()).length,
          partiesInvolved: parties.length > 0 ? parties : undefined,
          legalSignificance,
          confidenceScore: 1.0,
          extractionMethod: 'manual' as const,
          isUserModified: true
        } as any)).unwrap();
      } else {
        // Update existing event
        await dispatch(DBTimelineEventThunks.patch({
          entity: {
            _id: event!._id as string,
            title,
            description,
            startDate: convertToDateTime(startDate, startTime),
            startTime: startTime || undefined,
            endDate: endDate ? convertToDateTime(endDate, endTime) : undefined,
            endTime: endTime || undefined,
            eventType,
            legalSignificance,
            partiesInvolved: parties.length > 0 ? parties : undefined,
            isUserModified: true,
            // Set confidence to 1.0 (100%) since user has manually verified/edited the content
            confidenceScore: 1.0
          }
        } as any)).unwrap();
      }

      onSave();
    } catch (error) {
      console.error('Error saving event:', error);
      alert(t('common.timeline.edit.errorSaving'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('common.timeline.edit.confirmDelete'))) {
      return;
    }

    setIsDeleting(true);
    try {
      await dispatch(DBTimelineEventThunks.delete({
        entity: { _id: event._id as string },
        options: {}
      } as any)).unwrap();

      onDelete();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert(t('common.timeline.edit.errorDeleting'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {/* Scrollable form content */}
      <div className="mb-6">
        <DialogTitle className="text-xl font-bold text-slate-900">
          {event ? t('common.timeline.editEvent.editTitle') : t('common.timeline.editEvent.addTitle')}
        </DialogTitle>
        <DialogDescription className="text-slate-500 font-medium">
          {event ? t('common.timeline.editEvent.editDescription') : t('common.timeline.editEvent.addDescription')}
        </DialogDescription>
      </div>

      <div className="grid grid-cols-2 gap-4 pb-4">
        <div className="col-span-2">
          <Label htmlFor="title" className="text-xs">
            {t('common.timeline.edit.title')} <span className="text-red-500">*</span>
          </Label>
          <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('common.timeline.edit.titlePlaceholder')} className="mt-1" />
        </div>

        <div className="col-span-2">
          <Label htmlFor="description" className="text-xs">{t('common.timeline.edit.description')}</Label>
          <Textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('common.timeline.edit.descriptionPlaceholder')} className="mt-1 min-h-[100px] resize-none" />
        </div>

        <div>
          <Label htmlFor="startDate" className="text-xs">{t('common.timeline.edit.startDate')}</Label>
          <Input id="startDate" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1" />
        </div>

        <div>
          <Label htmlFor="startTime" className="text-xs">{t('common.timeline.edit.startTime')}</Label>
          <Input id="startTime" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="mt-1" />
        </div>

        <div>
          <Label htmlFor="endDate" className="text-xs">{t('common.timeline.edit.endDate')}</Label>
          <Input id="endDate" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1" />
        </div>

        <div>
          <Label htmlFor="endTime" className="text-xs">{t('common.timeline.edit.endTime')}</Label>
          <Input id="endTime" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="mt-1" />
        </div>

        <div>
          <Label htmlFor="eventType" className="text-xs">{t('common.timeline.edit.eventType')}</Label>
          <Select value={eventType} onValueChange={(val: any) => setEventType(val)}>
            <SelectTrigger id="eventType" className="mt-1"><SelectValue placeholder={t('common.timeline.edit.selectType')} /></SelectTrigger>
            <SelectContent className="z-[10001]">
              {eventTypes.map((type) => <SelectItem key={type} value={type}>{t(`common.timeline.eventTypes.${type}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="significance" className="text-xs">{t('common.timeline.edit.significance')}</Label>
          <Select value={legalSignificance || 'none'} onValueChange={(val: any) => setLegalSignificance(val === 'none' ? undefined : val)}>
            <SelectTrigger id="significance" className="mt-1"><SelectValue placeholder={t('common.timeline.edit.selectSignificance')} /></SelectTrigger>
            <SelectContent className="z-[10001]">
              <SelectItem value="none">{t('common.timeline.edit.none')}</SelectItem>
              <SelectItem value="high" className="text-red-600 font-bold">{t('common.timeline.edit.levels.high')}</SelectItem>
              <SelectItem value="medium" className="text-amber-600 font-bold">{t('common.timeline.edit.levels.medium')}</SelectItem>
              <SelectItem value="low" className="text-blue-600 font-bold">{t('common.timeline.edit.levels.low')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-2">
          <Label htmlFor="parties" className="text-xs">{t('common.timeline.edit.partiesInvolved')}</Label>
          <div className="flex gap-2 mt-1">
            <Input value={newParty} onChange={(e) => setNewParty(e.target.value)} placeholder={t('common.timeline.edit.addPartyPlaceholder')} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddParty(); } }} />
            <Button type="button" onClick={handleAddParty} variant="secondary" className="gap-1 px-3"><Plus size={16} />{t('common.timeline.edit.add')}</Button>
          </div>
          {parties.length > 0 && (
            <div className="flex gap-1 flex-wrap mt-2">
              {parties.map((party, idx) => (
                <Badge key={idx} variant="outline" className="gap-1">{party}<X className="h-3 w-3 cursor-pointer" onClick={() => handleRemoveParty(idx)} /></Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 justify-between items-center pt-4 mt-4 pb-6 -mb-6 -mx-6 px-6 border-t border-border bg-background sticky bottom-0 z-10">
        {!createMode && (
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDelete}
            disabled={isSaving || isDeleting}
          >
            <Trash2 className="h-4 w-4 me-2" />
            {isDeleting ? t('common.timeline.edit.deleting') : t('common.timeline.edit.delete')}
          </Button>
        )}
        {createMode && <div />}
        <div className="flex gap-2 ms-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isSaving || isDeleting}
          >
            {t('common.buttons.cancel')}
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving || isDeleting || (createMode && !title.trim())}
            className="min-w-[100px]"
          >
            {isSaving ? (
              <>
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
                {t('common.timeline.edit.saving')}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 me-2" />
                {t('common.buttons.save')}
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}

